import { useEffect, useMemo, useRef, useState } from 'react';
import { SitterSetup } from './CareView.jsx';
import { api, uploadTankPhoto } from '../api.js';
import { LivestockSection } from './LivestockSection.jsx';
import { EquipmentSection } from './EquipmentSection.jsx';
import { ParametersSection } from './ParametersSection.jsx';
import { SharingPanel } from './SharingPanel.jsx';
import { useConfirm } from './ConfirmDialog.jsx';
import { getDisplayUnits, galToL, lToGal, volumeLabel, tempLabel, formatVolume } from '../units.js';
import {
  buildPreview,
  buildDeltaLabel,
  volumeInputText,
  volumeInputToGallons,
  buildCopy,
} from '../tank-build.js';
import { isFreshwaterTank, stageOptions, stageLabel } from '../first-run.js';
import { useConfig } from '../config.js';
import { tankSubtype } from '../../../shared/tank-subtype.js';
import { tankTypeOptions, typeChangeMessage } from '../tank-type.js';
import { coralsBlockTypeChange } from '../../../shared/type-change-corals.js';

// Per-tank settings — tank details (name, photo, age, etc) and per-param
// targets/ranges. The site-admin-controlled baselines live in app_config
// (editable at /admin); this sheet lets the tank owner override any of
// them and add custom parameters for their tank only.
//
// Tank detail edits go to PATCH /api/tanks/:id. Photo upload via
// POST /api/tanks/:id/photo, photo delete via DELETE same path. Param
// edits go to PATCH /api/tanks/:id/params. Reset button calls
// POST /api/tanks/:id/params/reset.
//
// Established date drives cycling status (tanks under 90 days get
// relaxed nutrient targets in the score), so editing it here is the
// real-deal "I set up this tank on X" record, not just metadata.
export function TankSettingsSheet({
  tank,
  user,
  onClose,
  onReload,
  onTankDeleted,
  initialSection = null,
  initialFocus = null,
  backLabel = 'Home',
  onSectionChange = null,
}) {
  const confirm = useConfirm();
  // ─── Tank-detail drafts ─────────────────────────────────
  const initialEstablished = tank.established_date
    ? String(tank.established_date).slice(0, 10)
    : '';
  const initialUnits = getDisplayUnits(tank);
  const initialVolDisplay =
    tank.volume_gallons != null
      ? initialUnits.volume === 'L'
        ? String(Number(galToL(tank.volume_gallons).toFixed(1)))
        : String(tank.volume_gallons)
      : '';
  const [details, setDetails] = useState({
    name: tank.name || '',
    subtitle: tank.subtitle || '',
    emoji: tank.emoji || '🌊',
    // Volume value is shown in the keeper's chosen display unit but
    // saves back to canonical gallons. The L↔gal round-trip is exact
    // (units.js anchors 1 gal = 3.785411784 L).
    volume_display: initialVolDisplay,
    volume_unit: initialUnits.volume,
    temp_unit: initialUnits.temp,
    established_date: initialEstablished,
    purpose: tank.purpose === 'quarantine' ? 'quarantine' : 'display',
    subtype: tankSubtype(tank) || '',
  });
  const taxonomy = useConfig()?.onboarding?.taxonomy;
  const currentType = tankSubtype(tank) || '';
  const typeOptions = tankTypeOptions(taxonomy, currentType);
  const typeChanged = !!details.subtype && details.subtype !== currentType;
  const typeBlocked =
    typeChanged && details.purpose !== 'quarantine'
      ? coralsBlockTypeChange(details.subtype, tank.livestock)
      : null;
  const typeLabelFor = (k) => {
    const o = typeOptions.find((x) => x.k === k);
    return o?.short_label || o?.label || '';
  };
  // A type the admin has since removed from the list still shows as itself.
  const typeKnown = !currentType || typeOptions.some((o) => o.k === currentType);
  const [detailsBusy, setDetailsBusy] = useState(false);
  const [detailsErr, setDetailsErr] = useState('');
  const [detailsMsg, setDetailsMsg] = useState('');
  // Chores the new tank type would not have seeded, offered for removal.
  const [oldChores, setOldChores] = useState([]);
  const removeChore = async (c) => {
    const ok = await confirm({
      title: `Remove "${c.title}"?`,
      body: 'It leaves the Tasks page. Your test results stay.',
      confirmLabel: 'Remove',
      destructive: true,
    });
    if (!ok) return;
    const id = c.id;
    try {
      await api(`/api/tanks/${tank.id}/tasks/${id}`, { method: 'DELETE' });
      setOldChores((list) => list.filter((c) => c.id !== id));
      onReload?.();
    } catch (e) {
      setDetailsErr(e.message || 'Could not remove that chore.');
    }
  };
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoVersion, setPhotoVersion] = useState(0); // bust cache after upload
  const [deleting, setDeleting] = useState(false);
  const fileRef = useRef(null);

  const setDetail = (field, value) => setDetails((d) => ({ ...d, [field]: value }));

  const saveDetails = async () => {
    const name = details.name.trim();
    if (!name) {
      setDetailsErr('Tank name is required.');
      return;
    }
    // The reason shows under the type already; here, that nothing was saved.
    if (typeBlocked) {
      setDetailsErr(
        'Nothing was saved. Pick another tank type, or set it back, and your other changes save.'
      );
      return;
    }
    // Volume input is in the keeper's display unit; storage is gallons.
    let volumeGallons = null;
    if (details.volume_display !== '') {
      const n = Number(String(details.volume_display).replace(',', '.'));
      if (Number.isFinite(n) && n > 0) {
        volumeGallons = details.volume_unit === 'L' ? lToGal(n) : n;
      }
    }
    const patch = {
      name: name.slice(0, 60),
      subtitle: details.subtitle.trim().slice(0, 120) || null,
      emoji: details.emoji.trim().slice(0, 8) || '🌊',
      volume_gallons: volumeGallons,
      established_date: details.established_date || null,
      // The display preference rides on the same PATCH; server-side
      // sanitizeDisplayUnits drops anything outside the allow-list.
      display_units: { volume: details.volume_unit, temp: details.temp_unit },
      purpose: details.purpose,
      ...(typeChanged && details.purpose !== 'quarantine' ? { subtype_slug: details.subtype } : {}),
    };
    setDetailsBusy(true);
    setDetailsErr('');
    setDetailsMsg('');
    try {
      const saved = await api(`/api/tanks/${tank.id}`, { method: 'PATCH', body: patch });
      await onReload();
      if (saved?.type_change) {
        // The server swapped the type word in the subtitle; the form must
        // show it, or the next Save would write the old word back.
        setDetails((d) => ({
          ...d,
          subtitle: saved.subtitle || '',
          subtype: saved.subtype_slug || d.subtype,
        }));
        // Long enough to read what moved; it stays until the next save.
        setDetailsMsg(typeChangeMessage(saved.type_change, saved.type));
        setOldChores(Array.isArray(saved.type_change.chores) ? saved.type_change.chores : []);
      } else {
        setDetailsMsg('Saved.');
        setTimeout(() => setDetailsMsg(''), 2000);
      }
    } catch (e) {
      setDetailsErr(e.message || 'Could not save tank details.');
    } finally {
      setDetailsBusy(false);
    }
  };

  const handlePhotoPick = () => fileRef.current?.click();
  const handlePhotoFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting same file
    if (!file) return;
    setPhotoBusy(true);
    setDetailsErr('');
    setDetailsMsg('');
    try {
      await uploadTankPhoto(tank.id, file);
      await onReload();
      setPhotoVersion((v) => v + 1);
      // Same confirmation contract as the text-field saves — without
      // it a successful upload was indistinguishable from a silent
      // failure (user-reported).
      setDetailsMsg('Photo updated.');
      setTimeout(() => setDetailsMsg(''), 2500);
    } catch (err) {
      setDetailsErr(err.message || 'Could not upload photo.');
    } finally {
      setPhotoBusy(false);
    }
  };
  const handlePhotoDelete = async () => {
    const ok = await confirm({
      title: 'Remove the tank photo?',
      body: 'You can upload a new one any time.',
      confirmLabel: 'Remove',
      destructive: true,
    });
    if (!ok) return;
    setPhotoBusy(true);
    setDetailsErr('');
    setDetailsMsg('');
    try {
      await api(`/api/tanks/${tank.id}/photo`, { method: 'DELETE' });
      await onReload();
      setPhotoVersion((v) => v + 1);
      setDetailsMsg('Photo removed.');
      setTimeout(() => setDetailsMsg(''), 2500);
    } catch (err) {
      setDetailsErr(err.message || 'Could not remove photo.');
    } finally {
      setPhotoBusy(false);
    }
  };

  const handleDeleteTank = async () => {
    const confirmation = prompt(
      `This deletes "${tank.name}" and every reading, task, supply, and photo on it. Type the tank name to confirm:`
    );
    if (confirmation == null) return;
    if (confirmation.trim() !== tank.name) {
      setDetailsErr('Tank name did not match — nothing deleted.');
      return;
    }
    // What lived in the tank is the keeper's record, so its history is
    // only deleted on an explicit second answer. Dismissing either
    // question never deletes it: the first falls through to the second,
    // and dismissing the second cancels the whole delete.
    let keepHistory = false;
    // Unknown (the lookup failed) is treated as "there may be history":
    // the questions are skipped only when the tank is known to have none.
    let count;
    try {
      const rows = await api(`/api/livestock/history?tank_id=${encodeURIComponent(tank.id)}`);
      count = Array.isArray(rows) ? rows.length : null;
    } catch {
      count = null;
    }
    if (count !== 0) {
      keepHistory = await confirm({
        title: 'Keep the livestock history?',
        body:
          count == null
            ? "Keep this tank's livestock, with photos and notes, in your livestock history after the tank is deleted."
            : `This tank has ${count} livestock ${count === 1 ? 'record' : 'records'}. Keep ${count === 1 ? 'it' : 'them'}, with photos and notes, in your livestock history after the tank is deleted.`,
        confirmLabel: 'Keep the history',
        cancelLabel: 'No',
      });
      if (!keepHistory) {
        const sure = await confirm({
          title: 'Delete the livestock history too?',
          body: 'Every resident of this tank, their photos and notes will be gone for good.',
          confirmLabel: 'Delete it all',
          cancelLabel: 'Cancel',
          destructive: true,
        });
        if (!sure) return;
      }
    }
    setDeleting(true);
    setDetailsErr('');
    try {
      await api(`/api/tanks/${tank.id}${keepHistory ? '?keep_history=1' : ''}`, {
        method: 'DELETE',
      });
      // onTankDeleted closes settings itself once another tank is chosen.
      // Closing first rendered Home on the deleted tank (six 404s) and then
      // closed a second layer.
      if (typeof onTankDeleted === 'function') await onTankDeleted();
      else onClose();
    } catch (e) {
      setDetailsErr(e.message || 'Could not delete tank.');
      setDeleting(false);
    }
  };

  const photoUrl = tank.has_photo ? `/api/tanks/${tank.id}/photo?v=${photoVersion}` : null;

  const [subView, setSubView] = useState(initialSection); // null = menu; else section key
  // Account opens as a page over this one (from Equipment's HYDROS link):
  // the shell keeps the section so closing Account lands back on it.
  useEffect(() => {
    onSectionChange?.(subView);
  }, [subView]);
  // Opened straight into a section (Home's "Pick probes"): back returns Home,
  // like the browser's back, not to a menu the keeper never saw.
  const [openedAt] = useState(initialSection);
  const backToMenu = subView !== null && subView !== openedAt;

  const SECTIONS = [
    {
      k: 'details',
      label: 'Tank details',
      hint: 'Name, photo, volume, established date',
      summary: `${
        tank.volume_gallons
          ? formatVolume(tank.volume_gallons, getDisplayUnits(tank).volume)
          : 'no volume'
      }${tank.established_date ? ' · ' + ageHint(tank.established_date) : ''}`,
    },
    {
      k: 'stage',
      label: 'Set the stage',
      hint: 'Cycling, established and so on. Sensei works it out; change it if it is wrong',
      summary: tank.phase_override
        ? `Set to ${stageLabel(tank.phase_override) || tank.phase_override}`
        : 'Sensei decides',
    },
    {
      k: 'build',
      label: 'Tank build',
      hint: isFreshwaterTank(tank)
        ? 'Gravel, rock, wood → real working volume'
        : 'Sump, rock, sand → real working volume',
      summary:
        tank.volume_water_gal != null
          ? `${formatVolume(tank.volume_water_gal, getDisplayUnits(tank).volume)} working`
          : 'using label volume',
    },
    {
      k: 'livestock',
      label: 'Livestock',
      hint: isFreshwaterTank(tank)
        ? 'Fish, shrimp, snails in this tank'
        : 'Fish, coral, inverts in this tank',
      summary: `${(tank.livestock || []).length} entries`,
    },
    {
      k: 'equipment',
      label: 'Equipment and devices',
      hint: isFreshwaterTank(tank)
        ? 'Filter, heater, lighting, pumps, connected devices'
        : 'Skimmer, pumps, RO/DI, lighting, connected devices',
      summary: `${(tank.equipment || []).length} pieces`,
    },
    {
      k: 'parameters',
      label: 'Parameters',
      hint: 'Per-param targets, ranges, custom params',
      summary: 'configure',
    },
    {
      k: 'sharing',
      label: 'Sharing',
      hint:
        tank.role === 'owner'
          ? 'Invite a tank partner or maintenance helper'
          : `you have ${tank.role || 'no'} access`,
      summary: tank.role === 'owner' ? 'invite + manage' : `role: ${tank.role || 'none'}`,
    },
    ...(tank.role === 'owner'
      ? [
          {
            k: 'sitter',
            label: 'Sitter mode',
            hint: 'A private link for someone looking after the tank while you are away',
            summary: 'links + checklist',
          },
        ]
      : []),
    {
      k: 'export',
      label: 'Export',
      hint: 'Download this tank as an Excel spreadsheet',
      summary: 'Excel (.xlsx)',
    },
    {
      k: 'danger',
      label: 'Danger zone',
      hint: 'Delete tank',
      summary: 'destructive',
      danger: true,
    },
  ];
  const visibleSections = sectionsForRole(SECTIONS, tank.role);
  const activeSection = visibleSections.find((s) => s.k === subView);
  const can = (k) => visibleSections.some((s) => s.k === k);

  return (
    <div className="ts-page" aria-labelledby="ts-title">
      <header className="ts-page-header">
        <button
          type="button"
          aria-label={backToMenu ? 'Back to settings menu' : `Back to ${backLabel.toLowerCase()}`}
          className="ts-back"
          onClick={() => (backToMenu ? setSubView(null) : onClose())}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
          <span>{backToMenu ? 'Settings' : backLabel}</span>
        </button>
        <div className="ts-page-title">
          <div
            className="font-heading"
            style={{
              fontSize: 13,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: 'var(--text-muted)',
            }}
          >
            {activeSection ? activeSection.label : 'Tank settings'}
          </div>
          <h2
            id="ts-title"
            className="font-heading"
            style={{ fontSize: 18, fontWeight: 600, marginTop: 2 }}
          >
            {tank.name.toUpperCase()}
          </h2>
        </div>
      </header>

      <div className="ts-page-body">
        {detailsErr && subView !== null && (
          <div role="alert" className="ts-err">
            {detailsErr}
          </div>
        )}

        {subView === null && (
          <nav className="ts-menu" aria-label="Tank settings sections">
            <p className="ts-menu-intro">
              {tank.name} · {(tank.livestock || []).length} residents ·{' '}
              {(tank.equipment || []).length} pieces of gear
            </p>
            {tank.role && tank.role !== 'owner' && (
              <p className="ts-hint" style={{ margin: '0 0 12px' }}>
                Only the tank owner can change its details, build, parameters and stage, or delete
                it.
              </p>
            )}
            {visibleSections.map((s) => (
              <button
                key={s.k}
                type="button"
                className={`ts-menu-item ${s.danger ? 'ts-menu-danger' : ''}`}
                onClick={() => setSubView(s.k)}
              >
                <div className="ts-menu-body">
                  <div className="ts-menu-label font-heading">{s.label}</div>
                  <div className="ts-menu-hint">{s.hint}</div>
                </div>
                <div className="ts-menu-summary">
                  <span>{s.summary}</span>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </div>
              </button>
            ))}
          </nav>
        )}

        {subView === 'details' && can('details') && (
          <section className="ts-section">
            <div className="ts-section-head">
              <h3 className="font-heading">Tank details</h3>
              <p className="ts-hint">
                Name, photo, and how old the tank is. Setting an established date is what tells
                Sensei when to switch out of cycling mode.
              </p>
            </div>

            <div className="ts-photo-row">
              <div className="ts-photo-thumb" aria-hidden="true">
                {photoUrl ? (
                  <img src={photoUrl} alt="" />
                ) : (
                  <span className="ts-photo-emoji">{details.emoji || '🌊'}</span>
                )}
              </div>
              <div className="ts-photo-actions">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                  style={{ display: 'none' }}
                  onChange={handlePhotoFile}
                />
                <button
                  type="button"
                  className="ts-chip"
                  onClick={handlePhotoPick}
                  disabled={photoBusy}
                >
                  {photoBusy ? '…' : photoUrl ? 'Replace photo' : 'Upload photo'}
                </button>
                {photoUrl && (
                  <button
                    type="button"
                    className="ts-chip ts-chip-danger"
                    onClick={handlePhotoDelete}
                    disabled={photoBusy}
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>

            <div className="ts-row-body" style={{ marginTop: 14 }}>
              <label className="ts-field" style={{ minWidth: 180, flex: '1 1 180px' }}>
                <span>Name</span>
                <input
                  type="text"
                  value={details.name}
                  maxLength={60}
                  onChange={(e) => setDetail('name', e.target.value)}
                  placeholder="The 120"
                />
              </label>
              <label className="ts-field" style={{ width: 80 }}>
                <span>Emoji</span>
                <input
                  type="text"
                  value={details.emoji}
                  maxLength={8}
                  onChange={(e) => setDetail('emoji', e.target.value)}
                  placeholder="🌊"
                />
              </label>
            </div>

            <div className="ts-row-body">
              <label className="ts-field" style={{ minWidth: 240, flex: '1 1 240px' }}>
                <span>Subtitle</span>
                <input
                  type="text"
                  value={details.subtitle}
                  maxLength={120}
                  onChange={(e) => setDetail('subtitle', e.target.value)}
                  placeholder={isFreshwaterTank(tank) ? 'Planted community' : 'SPS-dominant reef'}
                />
              </label>
            </div>

            <div className="ts-row-body">
              <label className="ts-field">
                <span>Volume ({volumeLabel(details.volume_unit)})</span>
                <input
                  type="number"
                  step="any"
                  inputMode="decimal"
                  value={details.volume_display}
                  onChange={(e) => setDetail('volume_display', e.target.value)}
                  placeholder={details.volume_unit === 'L' ? '450' : '120'}
                />
              </label>
              <label className="ts-field" style={{ minWidth: 160 }}>
                <span>Established</span>
                <input
                  type="date"
                  value={details.established_date}
                  onChange={(e) => setDetail('established_date', e.target.value)}
                />
              </label>
            </div>

            {/* Per-tank display units. Storage stays canonical
                (gallons + °F); these toggles only flip what the
                keeper sees + types. Promise made on the onboarding
                size step: "you can change them later in Tank
                settings." Two segmented controls match that pattern. */}
            <div className="ts-row-body">
              <label className="ts-field" style={{ flex: '1 1 240px' }}>
                <span>Display units</span>
                <div className="ts-unit-row">
                  <UnitToggle
                    ariaLabel="Volume unit"
                    value={details.volume_unit}
                    options={[
                      { k: 'gal', label: 'gal' },
                      { k: 'L', label: 'L' },
                    ]}
                    onPick={(u) => {
                      // Convert the typed value so the displayed
                      // number lines up with the new unit. Empty
                      // stays empty.
                      const cur = String(details.volume_display).replace(',', '.');
                      const n = cur === '' ? null : Number(cur);
                      const converted =
                        !Number.isFinite(n) || n <= 0
                          ? cur
                          : u === 'L' && details.volume_unit === 'gal'
                            ? String(Number(galToL(n).toFixed(1)))
                            : u === 'gal' && details.volume_unit === 'L'
                              ? String(Number(lToGal(n).toFixed(1)))
                              : cur;
                      setDetails((d) => ({
                        ...d,
                        volume_unit: u,
                        volume_display: converted,
                      }));
                    }}
                  />
                  <UnitToggle
                    ariaLabel="Temperature unit"
                    value={details.temp_unit}
                    options={[
                      { k: 'F', label: tempLabel('F') },
                      { k: 'C', label: tempLabel('C') },
                    ]}
                    onPick={(u) => setDetail('temp_unit', u)}
                  />
                </div>
              </label>
            </div>
            <div className="ts-row-body">
              <label className="ts-field" style={{ flex: '1 1 240px' }}>
                <span>This tank is</span>
                <div className="ts-unit-row">
                  <UnitToggle
                    ariaLabel="What this tank is for"
                    value={details.purpose}
                    options={[
                      { k: 'display', label: 'Display' },
                      { k: 'quarantine', label: 'Quarantine or hospital' },
                    ]}
                    onPick={(v) => setDetail('purpose', v)}
                  />
                </div>
              </label>
            </div>
            {details.purpose !== 'quarantine' && typeOptions.length > 1 && (
              <div className="ts-row-body">
                <label className="ts-field" style={{ flex: '1 1 240px' }}>
                  <span>Tank type</span>
                  <select
                    className="ts-phase-select"
                    value={details.subtype}
                    onChange={(e) => {
                      setDetail('subtype', e.target.value);
                      setDetailsErr('');
                    }}
                  >
                    {!currentType && <option value="">Not set</option>}
                    {!typeKnown && <option value={currentType}>{tank.type || currentType}</option>}
                    {typeOptions.map((o) => (
                      <option key={o.k} value={o.k}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            )}
            {typeBlocked && (
              <p role="alert" className="ts-err" style={{ marginTop: 8 }}>
                {typeBlocked.error}
              </p>
            )}
            {details.purpose !== 'quarantine' && typeChanged && !typeBlocked && (
              <p className="ts-hint">
                Saving changes the type to {typeLabelFor(details.subtype)}. The tests and ranges
                that come with that type follow it. Ranges you set yourself stay, and no reading or
                history is removed.
              </p>
            )}
            {details.purpose === 'quarantine' && (
              <p className="ts-hint">
                Only temperature, ammonia, nitrite and pH are graded and alerted in a quarantine
                tank. Everything else is logged, so low salinity during hyposalinity is not an
                alarm, and no new reef chores are added. Chores already on this tank stay until you
                remove them on the Tasks page. Saving adds ammonia and nitrite cards if the tank has
                none.
              </p>
            )}
            {/* At the foot of the form: on a phone the units, purpose and type
                sit below the fold, and a Save above them went unseen. */}
            <div className="ts-row-body">
              <button
                type="button"
                className="ts-save"
                onClick={saveDetails}
                disabled={detailsBusy}
              >
                {detailsBusy ? '…' : 'Save'}
              </button>
            </div>
            {/* Beside Save as well: on a phone the alert at the top is off
                screen when this button is tapped. */}
            {detailsErr && (
              <div role="alert" className="ts-err" style={{ marginTop: 8 }}>
                {detailsErr}
              </div>
            )}
            {detailsMsg && (
              <p className="ts-hint" style={{ marginTop: 8, color: 'var(--ok, #4ade80)' }}>
                {detailsMsg}
              </p>
            )}
            {oldChores.length > 0 && (
              <div className="ts-old-chores">
                <p className="ts-hint">
                  These chores were for the old type. Remove the ones you no longer do; the rest
                  stay on the Tasks page.
                </p>
                <ul>
                  {oldChores.map((c) => (
                    <li key={c.id}>
                      <span>{c.title}</span>
                      <button
                        type="button"
                        className="ts-old-chore-remove font-heading"
                        onClick={() => removeChore(c)}
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        )}

        {subView === 'stage' && can('stage') && (
          <PhaseStageSection tank={tank} onReload={onReload} />
        )}

        {subView === 'build' && can('build') && (
          <TankBuildSection tank={tank} onReload={onReload} />
        )}

        {subView === 'livestock' && <LivestockSection tank={tank} onReload={onReload} />}

        {subView === 'equipment' && (
          <EquipmentSection
            tank={tank}
            onReload={onReload}
            user={user}
            initialDevice={openedAt === 'equipment' ? initialFocus : null}
          />
        )}

        {subView === 'parameters' && can('parameters') && (
          <ParametersSection
            tank={tank}
            onReload={onReload}
            onClose={onClose}
            focusKey={openedAt === 'parameters' ? initialFocus : null}
          />
        )}

        {subView === 'sharing' && (
          <section className="ts-section">
            <div className="ts-section-head">
              <h3 className="font-heading">Sharing</h3>
              <p className="ts-hint">
                Give a tank partner, maintenance helper, or friend access to this tank. Viewers see
                everything but can't write; editors can log daily data but can't delete the tank or
                manage who else has access.
              </p>
            </div>
            <SharingPanel tank={tank} onReload={onReload} />
          </section>
        )}

        {subView === 'sitter' && tank.role === 'owner' && (
          <div className="care-scope">
            <SitterSetup tank={tank} />
          </div>
        )}

        {subView === 'export' && <ExportSection tank={tank} />}

        {subView === 'danger' && can('danger') && (
          <section className="ts-section ts-danger-zone">
            <div className="ts-section-head">
              <h3 className="font-heading">Danger zone</h3>
              <p className="ts-hint">
                Deleting a tank removes every reading, task, supply, and photo logged against it.
                Cannot be undone.
              </p>
            </div>
            <button
              type="button"
              onClick={handleDeleteTank}
              disabled={deleting}
              className="ts-reset ts-chip-danger"
            >
              {deleting ? 'Deleting…' : 'Delete this tank'}
            </button>
          </section>
        )}
      </div>
    </div>
  );
}

// ─── Tank build (working-water computation) ───────────────────────────
// The single biggest accuracy problem in any reef-app dose calculator is
// that "150 gallon tank" is the label, not the water. Rock displaces;
// sand displaces; the sump never fills to the top. This section asks
// Sections only the owner can save: a tank partner or helper saw Save and
// Delete buttons that could only fail with "Only the tank owner can ...".
const OWNER_ONLY_SECTIONS = new Set(['details', 'stage', 'build', 'parameters', 'danger']);
export function sectionsForRole(sections, role) {
  if (!role || role === 'owner') return sections;
  return sections.filter((s) => !OWNER_ONLY_SECTIONS.has(s.k));
}

// Friendly relative age for the settings menu summary row.
function ageHint(established_date) {
  if (!established_date) return '';
  const ms = Date.now() - new Date(established_date).getTime();
  if (!Number.isFinite(ms)) return '';
  const days = Math.max(0, Math.round(ms / 86400000));
  if (days < 30) return `${days}d old`;
  if (days < 365) return `${Math.round(days / 30)}mo old`;
  return `${Math.round((days / 365) * 10) / 10}y old`;
}

// for those numbers in plain terms and shows the computed working
// volume the dose calculator will use, so the user sees WHY.
function TankBuildSection({ tank, onReload }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  // Inputs and results in the tank's own unit; storage is gallons.
  const unit = getDisplayUnits(tank).volume;
  const [build, setBuild] = useState({
    sump_gal: volumeInputText(tank.sump_gal, unit),
    rock_density: tank.rock_density || 'medium',
    sand_depth: tank.sand_depth || 'shallow',
    ato_reservoir_gal: volumeInputText(tank.ato_reservoir_gal, unit),
  });

  const preview = useMemo(
    () =>
      buildPreview({
        displayGal: tank.volume_gallons,
        sumpGal: volumeInputToGallons(build.sump_gal, unit),
        rock: build.rock_density,
        sand: build.sand_depth,
        unit,
      }),
    [tank.volume_gallons, build, unit]
  );

  const set = (k, v) => setBuild((b) => ({ ...b, [k]: v }));

  const save = async () => {
    setBusy(true);
    setErr('');
    setMsg('');
    try {
      await api(`/api/tanks/${tank.id}`, {
        method: 'PATCH',
        body: {
          sump_gal: volumeInputToGallons(build.sump_gal, unit),
          rock_density: build.rock_density,
          sand_depth: build.sand_depth,
          ato_reservoir_gal: volumeInputToGallons(build.ato_reservoir_gal, unit),
        },
      });
      await onReload();
      setMsg('Saved.');
      setTimeout(() => setMsg(''), 2000);
    } catch (e) {
      setErr(e.message || 'Could not save tank build.');
    } finally {
      setBusy(false);
    }
  };

  const Chip = ({ field, value, label, sub }) => (
    <button
      type="button"
      onClick={() => set(field, value)}
      className={`ts-chip-toggle ${build[field] === value ? 'active' : ''}`}
      aria-pressed={build[field] === value}
    >
      <span className="ts-chip-toggle-label">{label}</span>
      {sub && <span className="ts-chip-toggle-sub">{sub}</span>}
    </button>
  );

  const copy = buildCopy(unit, { freshwater: isFreshwaterTank(tank) });

  return (
    <section className="ts-section">
      <div className="ts-section-head">
        <h3 className="font-heading">Tank build</h3>
        <p className="ts-hint">
          {copy.lead} These four numbers let Sensei work out the actual water volume — the number
          that matters for dosing and water changes.
        </p>
      </div>

      <div className="tb-result">
        <div className="tb-result-label">Working water volume</div>
        <div className="tb-result-value">
          {formatVolume(preview.working, unit).split(' ')[0]}
          <span className="tb-result-unit">{volumeLabel(unit)}</span>
        </div>
        <div className="tb-result-breakdown">{preview.breakdown}</div>
        {Number.isFinite(preview.delta) && Math.abs(preview.delta) >= 1 && (
          <div className={`tb-result-delta ${preview.delta < 0 ? 'tb-delta-down' : 'tb-delta-up'}`}>
            {buildDeltaLabel(preview.working, tank.volume_gallons, unit)}
          </div>
        )}
      </div>

      <div className="tb-grid">
        <div className="tb-field">
          <label className="ts-field-label">Sump ({unit === 'L' ? 'litres' : 'gallons'})</label>
          <input
            type="number"
            inputMode="decimal"
            min="0"
            value={build.sump_gal}
            onChange={(e) => set('sump_gal', e.target.value)}
            placeholder="0 (no sump)"
          />
          <p className="ts-hint-sm">We use 60% of this — sumps never fill to the top.</p>
        </div>

        <div className="tb-field">
          <label className="ts-field-label">
            ATO reservoir ({unit === 'L' ? 'litres' : 'gallons'}, optional)
          </label>
          <input
            type="number"
            inputMode="decimal"
            min="0"
            value={build.ato_reservoir_gal}
            onChange={(e) => set('ato_reservoir_gal', e.target.value)}
            placeholder="0"
          />
          <p className="ts-hint-sm">Tracked for evaporation buffering, not for dosing math.</p>
        </div>

        <div className="tb-field tb-field-wide">
          <label className="ts-field-label">Rockscape density</label>
          <div className="tb-chips">
            <Chip field="rock_density" value="none" label="None" sub="bare bottom" />
            <Chip field="rock_density" value="light" label="Light" sub="−10%" />
            <Chip field="rock_density" value="medium" label="Medium" sub="−18%" />
            <Chip field="rock_density" value="heavy" label="Heavy" sub={copy.rockHeavy} />
          </div>
        </div>

        <div className="tb-field tb-field-wide">
          <label className="ts-field-label">Sand bed</label>
          <div className="tb-chips">
            <Chip field="sand_depth" value="bare" label="Bare" sub="0%" />
            <Chip field="sand_depth" value="shallow" label="Shallow" sub={copy.sandShallow} />
            <Chip field="sand_depth" value="deep" label="Deep" sub={copy.sandDeep} />
          </div>
        </div>
      </div>

      <div className="tb-actions">
        <button type="button" className="ts-save" onClick={save} disabled={busy}>
          {busy ? 'Saving…' : 'Save tank build'}
        </button>
      </div>
      {err && (
        <p className="ts-hint" style={{ marginTop: 8, color: 'var(--alert)' }}>
          {err}
        </p>
      )}
      {msg && (
        <p className="ts-hint" style={{ marginTop: 8, color: 'var(--ok, #4ade80)' }}>
          {msg}
        </p>
      )}
    </section>
  );
}

// "Set the stage" — single control that lets the user correct the
// derived phase when it's wrong (migrated tank, restart, etc). The app
// always derives a phase from age + readings + livestock; this is the
// editable pin that overrides the derivation. Empty selection = let the
// system derive automatically. Behaviour matches the spec: re-editable
// any time, no auto-expiry.
function PhaseStageSection({ tank, onReload }) {
  const current = tank.phase_override || '';
  const [value, setValue] = useState(current);
  const options = stageOptions(tank);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');

  const save = async (next) => {
    setBusy(true);
    setErr('');
    setMsg('');
    try {
      await api(`/api/tanks/${tank.id}`, {
        method: 'PATCH',
        body: { phase_override: next || null },
      });
      await onReload();
      setMsg(next ? 'Stage set.' : 'Cleared. Sensei will work it out again.');
      setTimeout(() => setMsg(''), 2000);
    } catch (e) {
      setErr(e.message || 'Could not save.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="ts-section">
      <div className="ts-section-head">
        <h3 className="font-heading">Set the stage</h3>
        <p className="ts-hint">
          Sensei works out your tank&rsquo;s stage from its age, readings and livestock. The stage
          changes which tests are graded and which chores are suggested. If it is wrong (a restarted
          tank, or rock and water moved over from an old one), pick the right one here. &ldquo;Let
          Sensei decide&rdquo; goes back to working it out.
        </p>
      </div>
      <div className="ts-field-row">
        <label className="ts-field" style={{ minWidth: 200 }}>
          <span>Stage</span>
          <select
            className="ts-phase-select"
            value={value}
            onChange={(e) => {
              const v = e.target.value;
              setValue(v);
              save(v);
            }}
            disabled={busy}
          >
            <option value="">Let Sensei decide</option>
            {options.map((o) => (
              <option key={o.k} value={o.k}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      <ul className="ts-stage-list" style={{ listStyle: 'none', padding: 0, margin: '8px 0 0' }}>
        {options.map((o) => (
          <li key={o.k} className="ts-hint" style={{ margin: '6px 0 0' }}>
            <strong>{o.label}:</strong> {o.line}
          </li>
        ))}
      </ul>
      {err && (
        <p className="ts-hint" style={{ marginTop: 8, color: 'var(--alert)' }}>
          {err}
        </p>
      )}
      {msg && (
        <p className="ts-hint" style={{ marginTop: 8, color: 'var(--ok, #4ade80)' }}>
          {msg}
        </p>
      )}
    </section>
  );
}

// Download the whole tank — setup + logged data — as an .xlsx workbook
// (one sheet each for parameters, livestock, equipment, supplies,
// readings, tasks, dosing). We fetch the blob rather than link straight
// to the route so a failure surfaces inline instead of navigating the
// SPA away, and so we can honour the server's Content-Disposition name.
function ExportSection({ tank }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const filenameFrom = (res, fallback) => {
    const cd = res.headers.get('Content-Disposition') || '';
    const m = /filename="?([^";]+)"?/i.exec(cd);
    return m ? m[1] : fallback;
  };

  const download = async () => {
    setBusy(true);
    setErr('');
    try {
      const res = await fetch(`/api/tanks/${tank.id}/export`, { credentials: 'include' });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Export failed (HTTP ${res.status})`);
      }
      let blob;
      try {
        blob = await res.blob();
      } catch {
        // iOS says only "Load failed" when the file stops partway.
        throw new Error('The download stopped partway. Check your connection and try again.');
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filenameFrom(res, 'tanksensei-tank.xlsx');
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErr(e.message || 'Could not export this tank.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="ts-section">
      <div className="ts-section-head">
        <h3 className="font-heading">Export</h3>
        <p className="ts-hint">
          Download this tank as an Excel spreadsheet, one sheet each: the setup (parameters,
          livestock, equipment, supplies, tasks), every reading, dose and water change, your
          journal, ICP results and quarantine logs. Times are on your clock, temperatures and
          volumes in this tank&apos;s units. Photos and videos stay in the app. Yours to keep,
          share, or back up.
        </p>
      </div>
      <button type="button" className="ts-save" onClick={download} disabled={busy}>
        {busy ? 'Preparing…' : 'Download Excel (.xlsx)'}
      </button>
      {busy && (
        <p className="ts-hint" style={{ marginTop: 8 }} role="status">
          A tank with years of probe readings takes a minute or two.
        </p>
      )}
      {err && (
        <p className="ts-hint" style={{ marginTop: 8, color: 'var(--alert)' }}>
          {err}
        </p>
      )}
    </section>
  );
}

// Two-option segmented toggle, used for the per-tank volume + temp
// units. Mirrors the same pattern as the onboarding SizeStep's unit
// picker so the keeper sees a consistent control.
function UnitToggle({ value, options, onPick, ariaLabel }) {
  return (
    <div className="ts-unit-toggle" role="group" aria-label={ariaLabel}>
      {options.map((o) => (
        <button
          key={o.k}
          type="button"
          className={`ts-unit-chip${value === o.k ? ' is-active' : ''}`}
          onClick={() => onPick(o.k)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
